import { Checkbox } from '@progress/kendo-react-inputs';
import { useCallback, useMemo } from 'react';
import { Tab, TabStrip } from 'src/components/design-system/TabStrip';
import { useContextSelector } from 'use-context-selector';
import { InvoiceScanManagerContext, initialOcrDataState } from './InvoiceScanManagerContext';
import InvoiceScanManagerTable from './InvoiceScanManagerTable';
import OcrQueueTable from './ocr/OcrQueueTable';

const InvoiceScanManagerTab = () => {
  const {
    setDataState,
    selectedView,
    setSelectedView,
    isOcrCompany,
    setOcrDataState,
    setDropdownFilter,
    invoiceScanManagerFilterInitialState,
    setOcrDropdownFilter,
    ocrFilterInitialState,
    ocrRecordCount,
    setRefreshCompany,
    setLatestClickedId,
    showDelete,
    setShowDelete,
    showUploadsOlderThan90Days,
    setShowUploadsOlderThan90Days,
  } = useContextSelector(InvoiceScanManagerContext, (context) => ({
    setDataState: context.setDataState,
    selectedView: context.selectedView,
    setSelectedView: context.setSelectedView,
    isOcrCompany: context.isOcrCompany,
    setOcrDataState: context.setOcrDataState,
    setDropdownFilter: context.setDropdownFilter,
    invoiceScanManagerFilterInitialState: context.invoiceScanManagerFilterInitialState,
    setOcrDropdownFilter: context.setOcrDropdownFilter,
    ocrFilterInitialState: context.ocrFilterInitialState,
    ocrRecordCount: context.ocrRecordCount,
    setRefreshCompany: context.setRefreshCompany,
    setLatestClickedId: context.setLatestClickedId,
    showDelete: context.showDelete,
    setShowDelete: context.setShowDelete,
    showUploadsOlderThan90Days: context.showUploadsOlderThan90Days,
    setShowUploadsOlderThan90Days: context.setShowUploadsOlderThan90Days,
  }));
  const ocrQueueTitle = useMemo(
    () => (isOcrCompany ? `OCR Queue (${ocrRecordCount || 0})` : 'OCR Queue'),
    [isOcrCompany, ocrRecordCount]
  );

  const handleSelectedView = useCallback(
    (e: number) => {
      if (selectedView === 0) {
        setDropdownFilter(invoiceScanManagerFilterInitialState);
        setDataState(initialOcrDataState);
      } else {
        setOcrDropdownFilter(ocrFilterInitialState);
        setOcrDataState(initialOcrDataState);
      }
      setSelectedView(e);
      setRefreshCompany?.((prev) => !prev);
      setLatestClickedId(0);
    },
    [
      selectedView,
      setSelectedView,
      setRefreshCompany,
      setLatestClickedId,
      setDropdownFilter,
      invoiceScanManagerFilterInitialState,
      setDataState,
      setOcrDropdownFilter,
      ocrFilterInitialState,
      setOcrDataState,
    ]
  );

  return (
    <>
      <div className="flex items-center justify-between gap-2 mt-[20px] mb-2 ml-[5px] flex-wrap">
        <TabStrip
          className="min-w-[190px]"
          selected={selectedView}
          onSelect={(e) => handleSelectedView(e.selected)}
        >
          <Tab className="text-sm min-w-[100px] text-center" title="Records" value={0} />
          <Tab className="text-sm min-w-[100px] text-center" title={ocrQueueTitle} value={1} />
        </TabStrip>

        {selectedView === 1 && isOcrCompany && (
          <p className="text-sm font-semibold">
            Average scan time 5 minutes or less for small batches. Please allow up to 4 hours for
            processing.
          </p>
        )}

        <div className="flex justify-end gap-2 mr-2">
          {selectedView === 0 && (
            <Checkbox
              label="Show Deleted"
              checked={showDelete}
              onChange={(e) => setShowDelete(e.value)}
              className="text-sm"
            />
          )}
          {/* Show checkbox unless: NOT an OCR company AND viewing OCR Queue tab */}
          {(isOcrCompany || selectedView !== 1) && (
            <Checkbox
              label="Show uploads older than 90 days"
              checked={showUploadsOlderThan90Days}
              onChange={(e) => setShowUploadsOlderThan90Days(e.value)}
              className="text-sm"
            />
          )}
        </div>
      </div>
      {selectedView === 0 ? <InvoiceScanManagerTable /> : <OcrQueueTable />}
    </>
  );
};
export default InvoiceScanManagerTab;
